import React, { useState } from 'react';
import { mutate } from 'swr';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../../components/ui';
import ApprovalTemplateIntegration from './ApprovalTemplateIntegration';
import { useCreateEntity, useUpdateEntity } from '../../../../hooks/useSWR';
import { approvalTemplatesConfig } from '../approvalTemplatesConfig';

/**
 * Custom Form Component for Approval Templates
 * This form uses the "Request Document Review and Approval Status" form (ApprovalIntegration)
 * adapted for template creation/editing
 */
const ApprovalTemplateForm = ({
  isOpen,
  formMode,
  formData: initialFormData,
  selectedRecord,
  setSelectedRecord,
  config,
  onClose,
  onSubmit,
  isLoading: externalLoading
}) => {
  const isEditMode = formMode === 'edit' && selectedRecord;
  const [isLoading, setIsLoading] = useState(false);
  
  // API hooks for template operations
  const baseEndpoint = config?.api?.baseEndpoint || '/approval-templates';
  const createEntity = useCreateEntity(baseEndpoint);
  const updateEntity = useUpdateEntity(baseEndpoint);

  const handleFormSubmit = async (approvalData) => {
    // Handle both event objects and data objects
    // If it's an event (from GenericFormsPage), prevent default and return early
    // ApprovalTemplateIntegration handles form submission via its own onSubmit
    if (approvalData && typeof approvalData.preventDefault === 'function') {
      approvalData.preventDefault();
      return; // Let ApprovalTemplateIntegration handle the form submission
    }
    
    // If it's not valid data from ApprovalTemplateIntegration, return early
    if (!approvalData || typeof approvalData !== 'object') {
      console.warn('ApprovalTemplateForm: Invalid approvalData received', approvalData);
      return;
    }
    
    console.log('ApprovalTemplateForm received approvalData:', approvalData);
    
    // Transform approval data to template data structure according to Prisma schema
    // The approvalData comes from ApprovalIntegration's backendData format
    // Note: dueDate is not included in templates - it will be calculated at request time
    
    // Determine approverType from the form data
    const approverType = approvalData.approverType || 'user';
    
    // Build steps array according to ApprovalTemplateStep model
    let steps = [];
    if (approvalData.approvers && approvalData.approvers.length > 0) {
      steps = approvalData.approvers.map((approverId, index) => {
        const stepNumber = index + 1;
        const stepType = approvalData.workflowType === 'review' 
          ? 'review'
          : approvalData.workflowType === 'review_approval' && index === 0
          ? 'review'
          : 'approval';
        
        const stepName = stepType === 'review'
          ? `Review Step ${stepNumber}`
          : `Approval Step ${stepNumber}`;
        
        // Build step according to ApprovalTemplateStep schema
        const step = {
          stepNumber: stepNumber,
          stepName: stepName,
          approverType: approverType, // user, role, or department
          stepType: stepType, // approval or review
          isRequired: true, // default true
          canDelegate: true, // default true
          escalationDays: 7 // default 7
        };
        
        // approverId is optional - only include if approverType is 'user' and approverId exists
        if (approverType === 'user' && approverId) {
          const parsedId = parseInt(approverId, 10);
          if (!isNaN(parsedId)) {
            step.approverId = parsedId;
          }
        }
        // For 'role' or 'department', approverId can be null or undefined
        // The backend will resolve the approver at request time
        
        return step;
      });
    } else if (selectedRecord?.steps && selectedRecord.steps.length > 0) {
      // Preserve existing steps when editing if no new approvers provided
      steps = selectedRecord.steps.map(step => ({
        stepNumber: step.stepNumber,
        stepName: step.stepName,
        approverType: step.approverType,
        approverId: step.approverId || null, // Explicitly set to null if undefined
        stepType: step.stepType || 'approval',
        isRequired: step.isRequired !== undefined ? step.isRequired : true,
        canDelegate: step.canDelegate !== undefined ? step.canDelegate : true,
        escalationDays: step.escalationDays || 7
      }));
    }
    
    const templateData = {
      name: approvalData.title || selectedRecord?.name || 'Untitled Template',
      description: approvalData.description || '',
      entityTypeId: approvalData.entityTypeId !== undefined ? approvalData.entityTypeId : (selectedRecord?.entityTypeId || null),
      priorityId: approvalData.priorityId || selectedRecord?.priorityId || null,
      approvalType: approvalData.approvalType || 'single',
      workflowType: approvalData.workflowType || 'approval',
      defaultDueDays: approvalData.defaultDueDays !== undefined ? approvalData.defaultDueDays : selectedRecord?.defaultDueDays || null,
      isActive: approvalData.isActive !== undefined ? approvalData.isActive : (selectedRecord?.isActive !== undefined ? selectedRecord.isActive : true),
      autoAssignRules: selectedRecord?.autoAssignRules || {}, // JSON field, default empty object
      steps: steps
    };

    console.log('Template form submitting:', templateData);
    
    setIsLoading(true);
    try {
      // Extract steps before applying fieldsToRemove (since steps is in fieldsToRemove)
      const stepsArray = templateData.steps || [];
      
      // Remove fields that shouldn't be sent to the API (tracking fields, relations, etc.)
      const fieldsToRemove = approvalTemplatesConfig.fieldsToRemove || [];
      const cleanedData = { ...templateData };
      fieldsToRemove.forEach(key => {
        delete cleanedData[key];
      });
      
      // Re-add steps after removing other fields (steps should be included in the API request)
      // Ensure steps array is properly formatted
      if (stepsArray && Array.isArray(stepsArray) && stepsArray.length > 0) {
        cleanedData.steps = stepsArray.map(step => {
          const cleanedStep = {
            stepNumber: step.stepNumber,
            stepName: step.stepName,
            approverType: step.approverType,
            stepType: step.stepType || 'approval',
            isRequired: step.isRequired !== undefined ? step.isRequired : true,
            canDelegate: step.canDelegate !== undefined ? step.canDelegate : true,
            escalationDays: step.escalationDays || 7
          };
          
          // Only include approverId if it's a number (for 'user' type)
          // For 'role' or 'department', don't include approverId or set it to null
          if (step.approverType === 'user' && step.approverId) {
            const approverId = parseInt(step.approverId, 10);
            if (!isNaN(approverId)) {
              cleanedStep.approverId = approverId;
            }
          }
          // For role/department, approverId should be null or omitted
          
          return cleanedStep;
        });
      } else {
        // If no steps, set to empty array (backend will handle this)
        cleanedData.steps = [];
      }
      
      console.log('Cleaned template data with steps:', cleanedData);

      // Create a modified fieldsToRemove that excludes 'steps' since we want to include it in the API request
      // The mutationFetcher will apply fieldsToRemove again, so we need to exclude 'steps' from it
      const fieldsToRemoveForAPI = fieldsToRemove.filter(field => field !== 'steps');
      
      console.log('Sending to API - cleanedData:', cleanedData, 'fieldsToRemoveForAPI:', fieldsToRemoveForAPI);

      let savedTemplate;
      if (isEditMode && selectedRecord?.id) {
        // Update existing template
        savedTemplate = await updateEntity(selectedRecord.id, cleanedData, fieldsToRemoveForAPI);
        console.log('Template updated successfully:', savedTemplate);
      } else {
        // Create new template
        savedTemplate = await createEntity(cleanedData, fieldsToRemoveForAPI);
        console.log('Template created successfully:', savedTemplate);
      }

      // Refresh the data list by invalidating the SWR cache
      const cacheKey = config?.api?.swrInvalidation?.mainList || baseEndpoint;
      mutate(cacheKey);
      
      // Update the selectedRecord in the split view with the saved data
      if (isEditMode && savedTemplate && setSelectedRecord) {
        setSelectedRecord(savedTemplate);
      }

      // Close the form
      onClose();
      
      // Don't call onSubmit from GenericFormsPage - we've handled the save ourselves
    } catch (error) {
      console.error('Error saving approval template:', error);
      alert(`Error saving approval template: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  // Prepare data for ApprovalIntegration component
  const getEntityType = () => {
    if (selectedRecord?.entityType?.name) {
      return selectedRecord.entityType.name;
    }
    if (selectedRecord?.entityTypeId) {
      // Map entity type ID to name (you may need to fetch this)
      return 'document';
    }
    return 'document';
  };

  const getEntityData = () => {
    return {
      documentTitle: selectedRecord?.name || 'Template Configuration',
      documentCode: `TEMPLATE-${selectedRecord?.id || 'NEW'}`,
      description: selectedRecord?.description || ''
    };
  };

  // Transform template to approval-like structure for the form
  const existingApprovalData = isEditMode && selectedRecord ? {
    id: selectedRecord.id,
    title: selectedRecord.name,
    description: selectedRecord.description,
    entityTypeId: selectedRecord.entityTypeId?.toString() || '',
    entityId: 0, // Template doesn't have entity ID
    priorityId: selectedRecord.priorityId,
    approvalType: selectedRecord.approvalType,
    workflowType: selectedRecord.workflowType,
    defaultDueDays: selectedRecord.defaultDueDays?.toString() || '',
    isActive: selectedRecord.isActive !== undefined ? selectedRecord.isActive : true,
    approvers: selectedRecord.steps?.map(step => step.approverId?.toString()).filter(Boolean) || [],
    approverType: selectedRecord.steps?.[0]?.approverType || 'user'
  } : null;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleFormCancel} size="lg">
      <ModalHeader>
        <ModalTitle>Request Document Review and Approval Status</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <ApprovalTemplateIntegration
          entityType={getEntityType()}
          entityId={selectedRecord?.id || 0}
          entityData={getEntityData()}
          existingTemplate={existingApprovalData}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isLoading || externalLoading}
          isEditMode={isEditMode}
        />
      </ModalContent>
    </Modal>
  );
};

export default ApprovalTemplateForm;

